import { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { AppSettingsProvider } from './lib/AppSettingsProvider';
import { PublicLayout } from './components/layout/PublicLayout';
import { AccountLayout } from './components/layout/AccountLayout';
import { AdminLayout } from './components/layout/AdminLayout';
import { CookieBanner } from './components/common/CookieBanner';
import { MaintenanceGate } from './components/common/MaintenanceGate';
import { RequireRole } from './components/common/RequireRole';
import { RequireAuth } from './components/common/RequireAuth';

// Public pages
import HomePage from './pages/public/HomePage';
import SobreRositaPage from './pages/public/SobreRositaPage';
import ProductsPage from './pages/public/ProductsPage';
import ProductDetailPage from './pages/public/ProductDetailPage';
import CartPage from './pages/public/CartPage';
import CheckoutPage from './pages/public/CheckoutPage';
import CheckoutConfirmationPage from './pages/public/CheckoutConfirmationPage';
import PrivacyPolicyPage from './pages/public/PrivacyPolicyPage';
import TermsPage from './pages/public/TermsPage';
import EntregasDevolucoesPage from './pages/public/EntregasDevolucoesPage';

// Auth
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';

// Account
import AccountDashboardPage from './pages/account/DashboardPage';
import AccountOrderDetailPage from './pages/account/OrderDetailPage';
import AccountProfilePage from './pages/account/ProfilePage';
import AccountAddressesPage from './pages/account/AddressesPage';

// Admin
import AdminDashboardPage from './pages/admin/DashboardPage';
import AdminProductsPage from './pages/admin/ProductsPage';
import AdminProductFormPage from './pages/admin/ProductFormPage';
import AdminOrdersPage from './pages/admin/OrdersPage';
import AdminOrderDetailPage from './pages/admin/OrderDetailPage';
import AdminProductSettingsPage from './pages/admin/product-settings';
import AdminCouponsPage from './pages/admin/CouponsPage';
import AdminShippingPage from './pages/admin/ShippingPage';
import AdminClientesPage from './pages/admin/ClientesPage';
import AdminEquipaPage from './pages/admin/EquipaPage';
import AdminEmailTemplatesPage from './pages/admin/EmailTemplatesPage';
import AdminEmailTemplateEditPage from './pages/admin/EmailTemplateEditPage';
import AdminEmailLogsPage from './pages/admin/EmailLogsPage';
import AdminEmailSettingsPage from './pages/admin/EmailSettingsPage';
import AdminSiteSettingsPage from './pages/admin/SiteSettingsPage';
import AdminPickupPointsPage from './pages/admin/PickupPointsPage';
import AdminPickupPointFormPage from './pages/admin/PickupPointFormPage';

import NotFoundPage from './pages/public/NotFoundPage';

export default function App(): JSX.Element {
  const fetchMe = useAuthStore((s) => s.fetchMe);
  const navigate = useNavigate();

  useEffect(() => {
    void fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    const onUnauthorized = (): void => {
      useAuthStore.getState().setUser(null);
      if (window.location.pathname.startsWith('/admin') || window.location.pathname.startsWith('/conta')) {
        navigate('/conta/login', { replace: true });
      }
    };
    window.addEventListener('rosita:unauthorized', onUnauthorized);
    return () => window.removeEventListener('rosita:unauthorized', onUnauthorized);
  }, [navigate]);

  return (
    <AppSettingsProvider>
      <MaintenanceGate>
      <Routes>
        {/* Public */}
        <Route element={<PublicLayout />}>
          <Route index element={<HomePage />} />
          <Route path="/sobre" element={<SobreRositaPage />} />
          <Route path="/produtos" element={<ProductsPage />} />
          <Route path="/produtos/:slug" element={<ProductDetailPage />} />
          <Route path="/carrinho" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/confirmacao" element={<CheckoutConfirmationPage />} />
          <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
          <Route path="/termos-e-condicoes" element={<TermsPage />} />
          <Route path="/entregas-e-devolucoes" element={<EntregasDevolucoesPage />} />

          {/* Auth (public) */}
          <Route path="/conta/login" element={<LoginPage />} />
          <Route path="/conta/registo" element={<RegisterPage />} />
          <Route path="/conta/recuperar" element={<ForgotPasswordPage />} />
          <Route path="/conta/nova-senha" element={<ResetPasswordPage />} />
          <Route path="/conta/verificar-email" element={<VerifyEmailPage />} />
        </Route>

        {/* Customer account */}
        <Route element={<RequireAuth />}>
          <Route element={<AccountLayout />}>
            <Route path="/conta" element={<AccountDashboardPage />} />
            <Route path="/conta/encomendas" element={<Navigate to="/conta" replace />} />
            <Route path="/conta/encomendas/:orderNumber" element={<AccountOrderDetailPage />} />
            <Route path="/conta/perfil" element={<AccountProfilePage />} />
            <Route path="/conta/moradas" element={<AccountAddressesPage />} />
          </Route>
        </Route>

        {/* Admin */}
        <Route
          element={
            <RequireRole roles={['store_manager', 'admin', 'super_admin']} />
          }
        >
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/produtos" element={<AdminProductsPage />} />
            <Route path="/admin/produtos/novo" element={<AdminProductFormPage />} />
            <Route
              path="/admin/produtos/configuracoes"
              element={<AdminProductSettingsPage />}
            />
            <Route path="/admin/produtos/:id" element={<AdminProductFormPage />} />
            <Route path="/admin/encomendas" element={<AdminOrdersPage />} />
            <Route
              path="/admin/encomendas/:orderNumber"
              element={<AdminOrderDetailPage />}
            />
            <Route
              path="/admin/categorias"
              element={
                <Navigate to="/admin/produtos/configuracoes?tab=categorias" replace />
              }
            />
            <Route path="/admin/cupoes" element={<AdminCouponsPage />} />
            <Route path="/admin/envios" element={<AdminShippingPage />} />
            <Route
              path="/admin/pontos-de-recolha"
              element={<AdminPickupPointsPage />}
            />
            <Route
              path="/admin/pontos-de-recolha/novo"
              element={<AdminPickupPointFormPage />}
            />
            <Route
              path="/admin/pontos-de-recolha/:id"
              element={<AdminPickupPointFormPage />}
            />
            <Route path="/admin/clientes" element={<AdminClientesPage />} />
            <Route path="/admin/equipa" element={<AdminEquipaPage />} />
            <Route element={<RequireRole roles={['admin', 'super_admin']} />}>
              <Route path="/admin/emails" element={<AdminEmailTemplatesPage />} />
              <Route
                path="/admin/emails/historico"
                element={<AdminEmailLogsPage />}
              />
              <Route
                path="/admin/emails/configuracoes"
                element={<AdminEmailSettingsPage />}
              />
              <Route
                path="/admin/emails/:key"
                element={<AdminEmailTemplateEditPage />}
              />
            </Route>
            <Route element={<RequireRole roles={['super_admin']} />}>
              <Route path="/admin/definicoes" element={<AdminSiteSettingsPage />} />
            </Route>
          </Route>
        </Route>

        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
      </MaintenanceGate>
      <CookieBanner />
    </AppSettingsProvider>
  );
}
